import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AuthViewModel } from '../auth.view-model';
import { EmptyError, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { LogService } from '../../core/log.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RouteManagerService } from '../../core/route-manager.service';
import { FHIRService } from '../../notification/fhir.service';
import { oauth2 } from 'fhirclient';
import { environment } from '../../../environments/environment';
import { AES, enc, HmacSHA256 } from 'crypto-js';
import { FHIRServiceInjectionToken } from 'src/app/notification/fhir-service.providers';

export const epicParamsKey = 'epicParams';

@Component({
  selector: 'app-loading',
  templateUrl: './loading.component.html',
  styleUrls: ['./loading.component.scss'],
})
export class LoadingComponent implements OnInit, OnDestroy {
  public destroy$ = new Subject();
  private fhirService = inject(FHIRServiceInjectionToken);

  constructor(
    private authViewModel: AuthViewModel,
    private route: ActivatedRoute,
    private log: LogService,
    private snackBar: MatSnackBar,
    private routeManager: RouteManagerService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      const auth = this.route.snapshot.queryParams['fhirAuthorizeError'] === 'true';
      if (auth) {
        this.log.error('FHIR authorization failed');
        this.snackBar.open(
          "Sorry, we couldn't load the information from EPIC. Please try again later, or enter it manually.",
          'Okay',
        );
        return;
      }
      const isInitialRequest = this.route.snapshot.queryParams['initial'] === 'true';
      console.log('isInitialRequest:', isInitialRequest);

      const fhirClient = !isInitialRequest ? await this.fhirService.getClient() : undefined;
      console.log('🚀 ~ LoadingComponent ~ ngOnInit ~ fhirClient:', fhirClient);

      let fhirAuthorizeError = false;

      if (fhirClient) {
        console.log('already authenticated');
        await this.addQueryParamsFromSessionStorage();
      } else {
        sessionStorage.clear();
        console.log('Session Storage Cleared');
        console.log('authorizing with FHIR');
        fhirAuthorizeError = await this.authorizeFhir();
      }

      this.routeManager.startManagement(fhirAuthorizeError ? { fhirAuthorizeError } : undefined);
    } catch (e: any) {
      this.log.error(e.message, e);
      this.snackBar.open('Something went wrong. Please close AFAS and try again.', 'Okay');
    }
  }

  public async authorizeFhir() {
    this.route.queryParams.subscribe(
      (queryParams) => {
        const { initial, ...params } = queryParams;
        console.log('🚀 ~ LoadingComponent ~ authorizeFhir ~ params:', params);
        console.log(params);
        if (params?.['patientId']) {
          sessionStorage.setItem('patientId', params?.['patientId']);
          sessionStorage.setItem('organizationKey', params?.['organizationKey']);
          const encryptedKey = HmacSHA256(epicParamsKey, params['patientId']).toString();
          const ciphertext = AES.encrypt(JSON.stringify(params), params['patientId']).toString();
          sessionStorage.setItem(encryptedKey, ciphertext);
        }
      },
      (error) => console.error(error),
    );

    try {
      const url = new URL(window.location.href);
      const origin = url.origin;

      await oauth2.authorize({
        client_id: environment.fhirClientId,
        scope: 'launch openid fhirUser',
        // if the redirectUri is not provided, the current url will be used
        redirectUri: environment.fhirRedirectUri || origin,
      });
      return false;
    } catch (e: any) {
      console.log('authentication error', e);
      this.log.error(e.message, e);
      return true;
    }
  }

  private async addQueryParamsFromSessionStorage() {
    const patient = await this.fhirService.getPatient();
    if (patient?.id) {
      const encryptedKey = HmacSHA256(epicParamsKey, patient.id).toString();
      const encryptedQueryParams = sessionStorage.getItem(encryptedKey);
      if (!encryptedQueryParams) return;
      const bytes = AES.decrypt(encryptedQueryParams, patient.id);
      const decryptedQueryParams = JSON.parse(bytes.toString(enc.Utf8));

      await this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { ...decryptedQueryParams },
        queryParamsHandling: 'merge',
      });
    }
  }

  private handleError(error: EmptyError | ErrorEvent | HttpErrorResponse): void {
    if (error instanceof ErrorEvent) {
      this.log.error('Clientside issue during login', error);
      this.snackBar.open(
        'There was an error during the login procedure.  We have been made aware of this and are working the issue',
        'Ok',
      );
    } else if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
    } else {
      this.snackBar.open(
        'AFAS was not provided your organization ID during launch and therefore could not log you in.  ' +
          'Please contact an administrator and let them know that AFAS requires their organization ID in the org query string parameter',
        'Ok',
      );
    }
  }

  private handleHttpError(error: HttpErrorResponse): void {
    switch (error.status) {
      case 404:
        this.snackBar.open('We could not resolve your organization in our database.', 'Ok');
        break;
      case 400:
        this.log.error(`400 response error during login.`);
        this.snackBar.open('The request to the servers could not be processed', 'Ok');
        break;
      default:
        this.snackBar.open(
          'We could not find a valid subscription for you organization.  Please contact us for help',
          'Ok',
        );
    }
  }

  ngOnDestroy() {
    this.routeManager.stopManagement();

    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
